import React, { useEffect, useMemo, useState } from 'react';
import { AlarmClockIcon, CheckIcon, DownloadIcon, LockKeyholeIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
import { PrimaryButton } from './PrimaryButton';
const checkout = 'https://esl-teacher-ready.impultienda.ar/checkout';
const includes = ['120 Ready-to-Teach English Lessons', '6 complete resources in one system', '40+ weeks without repeating', 'Every lesson fully structured end to end', 'Grammar, speaking, writing, and assessment included', 'Bonus 1 — 52 Weekly ESL Teaching Themes', 'Bonus 2 — Ultimate ESL Instruction Language Library', 'Bonus 3 — ESL Beginner Support Guide', 'Bonus 4 — 30 Emergency ESL Lessons', 'Bonus 5 — ESL Lesson Finder System', '7-day money-back guarantee'];
const bonuses = [['01', '52 Weekly ESL Teaching Themes', '12', '52 ready-to-use weekly themes with a central intention, teaching focus, class message, and closing reflection.', 'No preparation needed. Open, read, and teach.'], ['02', 'Ultimate ESL Instruction Language Library', '12', 'Ready-to-use teacher language for instructions, grammar explanations, corrections, encouragement, and opening and closing lessons.', 'The words are already there. You just say them.'], ['03', 'ESL Beginner Support Guide', '10', 'Accessible explanations, simplified instructions, and practical support for true beginners who need clarity, patience, and structure.', 'Have the right approach ready before class begins.'], ['04', '30 Emergency ESL Lessons', '12', 'Complete backup lessons for days when there is no time to plan, covering levels and topics with a full lesson structure.', 'You will never walk into a class unprepared again.'], ['05', 'ESL Lesson Finder System', '12', 'Choose the right lesson in under 60 seconds by level, topic, duration, group size, materials, and room energy.', 'The system does the choosing. You do the teaching.']];
function useCountdown() {
  const target = useMemo(() => Date.now() + 24 * 60 * 60 * 1000, []);
  const [remaining, setRemaining] = useState(target - Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setRemaining(Math.max(0, target - Date.now()));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [target]);
  return [Math.floor(remaining / 3600000), Math.floor(remaining % 3600000 / 60000), Math.floor(remaining % 60000 / 1000)] as const;
}
export function OfferSection() {
  const [hours, minutes, seconds] = useCountdown();
  return <>
      <section className="border-y border-line bg-charcoal px-4 py-16 sm:py-24" data-id="element-144">
        <div className="mx-auto max-w-5xl" data-id="element-145">
          <div className="text-center" data-id="element-146">
            <p className="text-xs font-bold tracking-[.18em] text-gold-300" data-id="element-147">
              INCLUDED AT NO ADDITIONAL COST
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-5xl" data-id="element-148">
              The 5 Strategic Bonuses
            </h2>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2" data-id="element-149">
            {bonuses.map(([number, title, value, description, benefit]) => <article key={number} className="relative rounded-2xl border border-gold-700/40 bg-black p-7" data-id="element-150">
                <span className="absolute right-0 top-0 rounded-bl-xl border-b border-l border-gold-700/40 bg-gold-700/10 px-3 py-2 text-xs font-bold text-gold-300" data-id="element-151">
                  BONUS {number}
                </span>
                <div className="grid h-11 w-11 place-items-center rounded-full bg-gold-700/20 text-xl text-gold-300" data-id="element-152">
                  +
                </div>
                <h3 className="mt-5 pr-14 font-display text-xl font-bold text-white" data-id="element-153">
                  {title}
                </h3>
                <p className="mt-3 text-xs text-stone-500" data-id="element-154">
                  Real value: <span className="line-through" data-id="element-155">${value}</span>{' '}
                  <strong className="ml-1 text-gold-300" data-id="element-156">→ FREE</strong>
                </p>
                <p className="mt-4 text-sm leading-6 text-stone-400" data-id="element-157">
                  {description}
                </p>
                <p className="mt-5 text-sm font-semibold italic text-gold-100" data-id="element-158">
                  {benefit}
                </p>
              </article>)}
          </div>

          <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-gold-700/50 bg-gold-700/10 p-7 text-center" data-id="element-159">
            <p className="text-sm text-stone-400" data-id="element-160">
              Total value of the 5 bonuses
            </p>
            <p className="mt-1 text-2xl text-stone-500 line-through" data-id="element-161">$58.00</p>
            <p className="mt-4 text-sm text-stone-300" data-id="element-162">Your price today</p>
            <p className="mt-1 font-display text-4xl font-bold text-gold-300" data-id="element-163">
              FREE
            </p>
            <p className="mt-2 text-xs text-gold-100" data-id="element-164">
              Included only with this offer.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-red-500/40 bg-[#1b0808] p-6 text-center shadow-[0_0_30px_rgba(239,68,68,.12)] sm:p-8" data-id="element-165">
            <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-red-300" data-id="element-166">
              <AlarmClockIcon size={19} data-id="element-167" />
              The launch price ends in:
            </p>
            <div className="mt-5 flex items-center justify-center gap-2 sm:gap-4" data-id="element-168">
              <TimeBox value={hours} label="hrs" data-id="element-169" />
              <span className="text-3xl font-bold text-red-400" data-id="element-170">:</span>
              <TimeBox value={minutes} label="min" data-id="element-171" />
              <span className="text-3xl font-bold text-red-400" data-id="element-172">:</span>
              <TimeBox value={seconds} label="sec" data-id="element-173" />
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-stone-300" data-id="element-174">
              After this the price goes up and it will not come back down.
            </p>
            <div className="mt-6 h-2 overflow-hidden rounded-full border border-red-500/30 bg-red-950" data-id="element-175">
              <div className="h-full w-[77%] rounded-full bg-red-500" data-id="element-176" />
            </div>
            <p className="mt-2 text-right text-xs text-red-300" data-id="element-177">
              77% of spots taken · Only a few left
            </p>
          </div>
        </div>
      </section>

      <section id="oferta" className="relative overflow-hidden bg-black px-4 py-16 sm:py-24" data-id="element-178">
        <div className="absolute left-1/2 top-1/2 -z-0 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold-700/15 blur-[120px]" data-id="element-179" />
        <div className="relative z-10 mx-auto max-w-3xl" data-id="element-180">
          <div className="text-center" data-id="element-181">
            <h2 className="font-display text-3xl font-bold text-white sm:text-5xl" data-id="element-182">
              Your Complete Offer Today
            </h2>
            <p className="mt-3 text-gold-100" data-id="element-183">
              The complete 6-in-1 teaching system + 5 strategic bonuses — ready
              for your next class.
            </p>
          </div>

          <div className="mt-10 rounded-3xl border-2 border-gold-500 bg-charcoal p-1 shadow-gold" data-id="element-184">
            <div className="relative rounded-[1.3rem] bg-black p-6 text-center sm:p-10" data-id="element-185">
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold-300 px-5 py-1.5 text-xs font-bold tracking-wide text-black" data-id="element-186">
                COMPLETE PACK
              </span>
              <h3 className="font-display text-3xl font-bold text-gold-300" data-id="element-187">
                ESL Teacher Ready™
              </h3>
              <p className="mt-3 text-stone-400" data-id="element-188">
                Everything you need to teach English with confidence.
              </p>

              <ul className="mx-auto mt-7 max-w-md space-y-2 rounded-xl border border-gold-700/40 bg-gold-700/10 p-5 text-left" data-id="element-189">
                {includes.map(item => <li key={item} className="flex gap-2 text-sm text-stone-200" data-id="element-190">
                    <CheckIcon size={16} className="mt-0.5 shrink-0 text-gold-300" data-id="element-191" />
                    {item}
                  </li>)}
              </ul>

              <div className="mt-7" data-id="element-192">
                <p className="text-lg text-stone-500 line-through" data-id="element-193">
                  USD 105.00
                </p>
                <p className="mt-1 text-xl text-white" data-id="element-194">
                  TODAY:{' '}
                  <strong className="text-4xl text-gold-300 sm:text-5xl" data-id="element-195">
                    USD 27.00
                  </strong>
                </p>
              </div>

              <PrimaryButton href={checkout} className="mt-7 w-full sm:w-auto" data-id="element-196">
                GET THE COMPLETE 6-IN-1 SYSTEM NOW
              </PrimaryButton>

              <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-stone-400" data-id="element-197">
                <span className="inline-flex items-center gap-1" data-id="element-198">
                  <LockKeyholeIcon size={13} className="text-gold-300" data-id="element-199" />
                  Secure payment
                </span>
                <span className="inline-flex items-center gap-1" data-id="element-200">
                  <ZapIcon size={13} className="text-gold-300" data-id="element-201" />
                  Immediate access
                </span>
                <span className="inline-flex items-center gap-1" data-id="element-202">
                  <ShieldCheckIcon size={13} className="text-gold-300" data-id="element-203" />
                  7-day guarantee
                </span>
              </div>

              <div className="mx-auto mt-5 flex max-w-md items-start gap-2 rounded-xl border border-line bg-charcoal p-3 text-left text-xs leading-5 text-stone-400" data-id="element-204">
                <DownloadIcon size={14} className="mt-0.5 shrink-0 text-gold-300" data-id="element-205" />
                Pay in your local currency. The amount adapts automatically
                based on the country you are purchasing from.
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}
function TimeBox({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="min-w-[72px] rounded-xl border border-red-500/40 bg-black px-3 py-3 sm:min-w-[104px]" data-id="element-206">
      <span className="text-3xl font-bold tabular-nums text-white sm:text-4xl" data-id="element-207">
        {String(value).padStart(2, '0')}
      </span>
      <span className="ml-1 text-xs text-red-300" data-id="element-208">{label}</span>
    </div>;
}